import { motion } from "framer-motion";
import heroVideo from "../assets/hero/hero.mp4";
import heroImage from "../assets/hero/hero.jpg";
import { useNavigate } from "react-router-dom";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1], delay },
});

export default function Hero() {
  const navigate = useNavigate();

  return (
    <section id="hero" className="relative min-h-screen flex items-center overflow-hidden">

      {/* Video background (desktop) */}
      <video
        className="hidden md:block absolute inset-0 w-full h-full object-cover"
        src={heroVideo}
        autoPlay
        muted
        loop
        playsInline
      />

      {/* Image fallback (mobile) */}
      <div
        className="md:hidden absolute inset-0"
        style={{
          backgroundImage: `url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* Layered overlay: dark base + left-side gradient for text legibility */}
      <div className="absolute inset-0 bg-black/55" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />

      {/* Subtle top vignette */}
      <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/40 to-transparent" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 w-full pt-24 pb-20">
        <div className="max-w-2xl">

          {/* Eyebrow label */}
          <motion.div {...fadeUp(0.1)} className="flex items-center gap-3 mb-6">
            <span className="block w-8 h-px bg-brand-lightGreen" />
            <span className="text-brand-lightGreen text-xs uppercase tracking-[0.2em] font-semibold">
              Corrugated Packaging
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            {...fadeUp(0.25)}
            className="font-heading text-5xl md:text-6xl lg:text-7xl leading-[1.05] text-white"
          >
            Precision-Built<br />
            <span className="text-brand-lightGreen italic">
              Packaging
            </span>{" "}
            <span className="text-white">
              Solutions
            </span>
          </motion.h1>

          {/* Subtext */}
          <motion.p
            {...fadeUp(0.4)}
            className="mt-6 text-base md:text-lg text-gray-300 leading-relaxed max-w-lg"
          >
            Engineered corrugated cartons built for durability, supply-chain efficiency,
            and brand protection — from single-wall to heavy-duty export grades.
          </motion.p>

          {/* CTA row */}
          <motion.div {...fadeUp(0.55)} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={() => navigate("/contact")}
              className="group relative px-8 py-3.5 bg-brand-green text-white uppercase text-sm tracking-widest font-semibold overflow-hidden transition-all duration-300 hover:bg-brand-blue"
            >
              {/* Shine sweep on hover */}
              <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%] bg-white/10 skew-x-12 transition-transform duration-500 ease-in-out" />
              Get a Quote
            </button>

            <button
              onClick={() => document.getElementById("products")?.scrollIntoView({ behavior: "smooth" })}
              className="flex items-center gap-2 text-white/70 hover:text-white text-sm uppercase tracking-widest transition-colors duration-200"
            >
              <span>View Products</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </button>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            {...fadeUp(0.7)}
            className="mt-14 flex flex-wrap gap-6 text-white/50 text-xs uppercase tracking-widest"
          >
            {["ISO Certified", "Export-Ready", "Custom Sizes", "Bulk Orders"].map((tag) => (
              <span key={tag} className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-brand-lightGreen inline-block" />
                {tag}
              </span>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5 text-white/40"
      >
        <span className="text-[10px] uppercase tracking-[0.2em]">Scroll</span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </motion.div>

    </section>
  );
}